import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { ArrowLeft, Plus, Edit, Building, Settings, Shield, Lock } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useMfaStepUp } from "@/hooks/useMfaStepUp";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useI18n } from "@/i18n/I18nProvider";
import LanguageSwitcher from "@/components/ui/LanguageSwitcher";
import { Seo } from "@/components/seo/Seo";
import { formatCurrency } from "@/lib/currency";

type TFn = (key: string, fallback?: string) => string;

const createCompanyUpdateSchema = (t: TFn) =>
  z.object({
  company_name: z.string().min(1, t("company_profile.validation.company_name")),
  description: z.string().min(10, t("company_profile.validation.description")),
  address: z.string().min(1, t("company_profile.validation.address")),
  city: z.string().min(1, t("company_profile.validation.city")),
  postal_code: z.string().min(1, t("company_profile.validation.postal_code")),
  tax_id: z.string().optional(),
  legal_name: z.string().optional(),
  vat_number: z.string().optional(),
  sdi_code: z.string().optional(),
  pec_email: z.string().email(t("company_profile.validation.pec_email")).optional().or(z.literal("")),
  phone: z.string().min(1, t("company_profile.validation.phone")),
  email: z.string().email(t("company_profile.validation.email")),
  website: z.string().url().optional().or(z.literal("")),
  website_url: z.string().url().optional().or(z.literal("")),
  facebook_url: z.string().url().optional().or(z.literal("")),
  linkedin_url: z.string().url().optional().or(z.literal("")),
  instagram_url: z.string().url().optional().or(z.literal("")),
  whatsapp_url: z.string().optional().or(z.literal("")),
});

type CompanyUpdateForm = z.infer<ReturnType<typeof createCompanyUpdateSchema>>;

interface Company {
  id: string;
  company_name: string;
  description: string;
  address: string;
  city: string;
  postal_code: string;
  tax_id: string;
  phone: string;
  email: string;
  website: string;
  rating: number;
  total_reviews: number;
  is_verified: boolean;
  logo_url?: string | null;
  cover_image_url?: string | null;
}

interface Service {
  id: string;
  name: string;
  description: string;
  category_id: string;
  base_price: number;
  pricing_type: string;
  duration_minutes: number;
  is_active: boolean;
  service_categories: {
    name: string;
  };
}

export default function CompanyProfile() {
  const { t, lang } = useI18n();
  const [company, setCompany] = useState<Company | null>(null);
  const [vendorConsent, setVendorConsent] = useState<boolean>(false);
  const [customerConsent, setCustomerConsent] = useState<boolean>(false);
  // MFA (TOTP) - Supabase Auth (per-user; exposed in company cabinet too)
  const [mfaLoading, setMfaLoading] = useState(false);
  const [mfaError, setMfaError] = useState<string>("");
  const [mfaFactors, setMfaFactors] = useState<any[]>([]);
  const [mfaEnrolled, setMfaEnrolled] = useState<any>(null);
  const [mfaChallenge, setMfaChallenge] = useState<any>(null);
  const [mfaCode, setMfaCode] = useState<string>("");
  const [mfaQrSvg, setMfaQrSvg] = useState<string>("");
  const [mfaUri, setMfaUri] = useState<string>("");
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { ensureAAL2, StepUpDialog } = useMfaStepUp();

  const isVerifiedTotp = (f: any) => {
    if (!f) return false;
    const type = String(f?.factor_type ?? f?.type ?? "").toLowerCase();
    const status = String(f?.status ?? f?.factor_status ?? "").toLowerCase();
    return type === "totp" && (status === "verified" || f?.verified === true);
  };

  const requireAAL2 = async () => {
    const ok = await ensureAAL2();
    if (!ok) {
      toast({
        title: t("company_profile.toast.verification_required"),
        description: t("company_profile.toast.verification_required_desc"),
        variant: "destructive",
      });
    }
    return ok;
  };

  const companyForm = useForm<CompanyUpdateForm>({
    resolver: zodResolver(createCompanyUpdateSchema(t)),
  });

  useEffect(() => {
    checkAuthAndFetchData();
    loadMfa();
  }, []);

  const loadMfa = async () => {
    try {
      setMfaError("");
      const mfa = (supabase as any)?.auth?.mfa;
      if (!mfa) return;
      const { data, error } = await mfa.listFactors();
      if (error) throw error;
      const factors = data?.totp || data?.all || [];
      setMfaFactors(Array.isArray(factors) ? factors : []);
    } catch (e: any) {
      setMfaError(e?.message || t("profile.mfa.load_failed"));
    }
  };

  const startEnable2FA = async () => {
    setMfaLoading(true);
    setMfaError("");
    setMfaCode("");
    setMfaEnrolled(null);
    setMfaChallenge(null);
    setMfaQrSvg("");
    setMfaUri("");
    try {
      const mfa = (supabase as any)?.auth?.mfa;
      if (!mfa) throw new Error("Supabase MFA not available");

      // Cleanup any leftover unverified factors (common if user started setup and closed modal)
      try {
        const { data: listData } = await mfa.listFactors();
        const all = (listData?.totp || listData?.all || []) as any[];
        const leftovers = (all || []).filter((f) => String(f?.factor_type ?? f?.type ?? "").toLowerCase() === "totp" && !isVerifiedTotp(f));
        for (const f of leftovers) {
          if (f?.id) {
            await mfa.unenroll({ factorId: f.id }).catch(() => {});
          }
        }
      } catch {}

      const friendlyName = `Halomora TOTP (${new Date().toISOString()})`;
      const { data: enrollData, error: enrollError } = await mfa.enroll({ factorType: "totp", friendlyName });
      if (enrollError) throw enrollError;
      setMfaEnrolled(enrollData);
      setMfaQrSvg(enrollData?.totp?.qr_code || "");
      setMfaUri(enrollData?.totp?.uri || "");
      const factorId = enrollData?.id;
      if (!factorId) throw new Error("Missing factor id");
      const { data: ch, error: chErr } = await mfa.challenge({ factorId });
      if (chErr) throw chErr;
      setMfaChallenge(ch);
    } catch (e: any) {
      setMfaError(e?.message || t("profile.mfa.setup_failed"));
    } finally {
      setMfaLoading(false);
    }
  };

  const verifyEnable2FA = async () => {
    setMfaLoading(true);
    setMfaError("");
    try {
      const mfa = (supabase as any)?.auth?.mfa;
      if (!mfa) throw new Error("Supabase MFA not available");
      const factorId = mfaEnrolled?.id;
      const challengeId = mfaChallenge?.id;
      if (!factorId || !challengeId) throw new Error("Missing challenge");
      const code = (mfaCode || "").replace(/\s/g, "");
      const { error } = await mfa.verify({ factorId, challengeId, code });
      if (error) throw error;
      await loadMfa();
      toast({ title: t("profile.toast.mfa_enabled"), description: t("profile.toast.mfa_enabled_desc") });
    } catch (e: any) {
      setMfaError(e?.message || t("profile.mfa.invalid_code"));
    } finally {
      setMfaLoading(false);
    }
  };

  const disable2FA = async () => {
    setMfaLoading(true);
    setMfaError("");
    try {
      const mfa = (supabase as any)?.auth?.mfa;
      if (!mfa) throw new Error("Supabase MFA not available");
      const verified = (mfaFactors || []).find((f: any) => isVerifiedTotp(f));
      const factorId = verified?.id || (mfaFactors || [])?.[0]?.id;
      if (!factorId) throw new Error("No factor to remove");
      const { error } = await mfa.unenroll({ factorId });
      if (error) throw error;
      await loadMfa();
      toast({ title: t("profile.toast.mfa_disabled_title") });
    } catch (e: any) {
      setMfaError(e?.message || t("profile.mfa.disable_failed"));
    } finally {
      setMfaLoading(false);
    }
  };

  const checkAuthAndFetchData = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate("/auth");
        return;
      }

      // Fetch company profile
      const { data: profile, error: profileError } = await supabase
        .from("profiles")
        .select(`
          *,
          companies (*)
        `)
        .eq("user_id", session.user.id)
        .single();

      if (profileError) throw profileError;

      // Determine the current company explicitly to avoid picking a deleted/old one
      let companyRow: any = null;
      try {
        // Prefer latest onboarding_completed=true and active/not-deleted
        const { data: completedList, error: compErr1 } = await (supabase as any)
          .from('companies')
          .select('*')
          .eq('profile_id', profile.id)
          .eq('is_deleted', false)
          .eq('is_active', true)
          .eq('onboarding_completed', true)
          .order('created_at', { ascending: false })
          .limit(1);
        if (compErr1) throw compErr1;
        if (completedList && completedList.length > 0) {
          companyRow = completedList[0];
        } else {
          const { data: activeList, error: compErr2 } = await (supabase as any)
            .from('companies')
            .select('*')
            .eq('profile_id', profile.id)
            .eq('is_deleted', false)
            .eq('is_active', true)
            .order('created_at', { ascending: false })
            .limit(1);
          if (compErr2) throw compErr2;
          companyRow = (activeList && activeList.length > 0) ? activeList[0] : null;
        }
      } catch (e) {
        // fall through
      }
      if (!companyRow) { navigate("/company/onboarding"); return; }

      setCompany(companyRow);
      setVendorConsent(!!(profile as any)?.vendor_consent);
      setCustomerConsent(!!(profile as any)?.customer_consent);
      companyForm.reset({
        company_name: companyRow.company_name,
        description: companyRow.description || "",
        address: companyRow.address,
        city: companyRow.city,
        postal_code: companyRow.postal_code || "",
        tax_id: companyRow.tax_id || "",
        legal_name: (companyRow as any).legal_name || "",
        vat_number: (companyRow as any).vat_number || "",
        sdi_code: (companyRow as any).sdi_code || "",
        pec_email: (companyRow as any).pec_email || "",
        phone: companyRow.phone,
        email: companyRow.email,
        website: companyRow.website || "",
        website_url: (companyRow as any).website_url || "",
        facebook_url: (companyRow as any).facebook_url || "",
        linkedin_url: (companyRow as any).linkedin_url || "",
        instagram_url: (companyRow as any).instagram_url || "",
        whatsapp_url: (companyRow as any).whatsapp_url || "",
      });

      // Fetch services
      const { data: servicesData, error: servicesError } = await supabase
        .from("services")
        .select(`
          *,
          service_categories (name)
        `)
        .eq("company_id", companyRow.id)
        .order("created_at", { ascending: false });

      if (servicesError) throw servicesError;
      setServices(servicesData || []);

    } catch (error) {
      toast({
        title: t("company_profile.toast.error"),
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const exportData = async () => {
    try {
      const ok = await ensureAAL2();
      if (!ok) return;
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { navigate('/auth'); return; }
      const { data, error } = await (supabase as any).rpc('export_my_data');
      if (error) throw error;
      const blob = new Blob([JSON.stringify(data || {}, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'halomora-data-export.json'; a.click();
      URL.revokeObjectURL(url);
      toast({ title: t("company_profile.toast.export_ready"), description: t("company_profile.toast.export_ready_desc") });
    } catch (e: any) {
      toast({ title: t("company_profile.toast.export_failed"), description: e.message || t("company_profile.toast.error"), variant: "destructive" });
    }
  };

  const deleteAccount = async () => {
    try {
      const ok = await ensureAAL2();
      if (!ok) return;
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { navigate('/auth'); return; }
      const { error } = await (supabase as any).rpc('request_account_deletion');
      if (error) throw error;
      toast({ title: t("company_profile.toast.account_deleted"), description: t("company_profile.toast.account_deleted_desc") });
      try { await supabase.auth.signOut(); } catch {}
      navigate('/');
    } catch (e: any) {
      toast({ title: t("company_profile.toast.delete_failed"), description: e.message || t("company_profile.toast.error"), variant: "destructive" });
    }
  };

  const handleCompanyUpdate = async (data: CompanyUpdateForm) => {
    try {
      if (!company) return;

      // Normalize links: trim, auto-prefix https://, handle wa.me
      const norm = (v?: string | null) => {
        const s = (v || "").trim();
        if (!s) return null;
        if (/^https?:\/\//i.test(s)) return s;
        if (/^wa\.me\//i.test(s)) return `https://${s}`;
        if (/^[a-z0-9][a-z0-9.-]+\.[a-z]{2,}/i.test(s)) return `https://${s}`;
        return s;
      };

      const { error } = await supabase
        .from("companies")
        .update({
          company_name: data.company_name,
          description: data.description,
          address: data.address,
          city: data.city,
          postal_code: data.postal_code,
          tax_id: data.tax_id || null,
          legal_name: data.legal_name || null,
          vat_number: data.vat_number || null,
          sdi_code: data.sdi_code || null,
          pec_email: data.pec_email || null,
          phone: data.phone,
          email: data.email,
          website: norm(data.website),
          website_url: norm(data.website_url),
          facebook_url: norm(data.facebook_url),
          linkedin_url: norm(data.linkedin_url),
          instagram_url: norm(data.instagram_url),
          whatsapp_url: norm(data.whatsapp_url),
        })
        .eq("id", company.id);

      if (error) throw error;

      toast({
        title: t("company_profile.toast.updated"),
        description: t("company_profile.toast.updated_desc"),
      });

      checkAuthAndFetchData();
    } catch (error) {
      toast({
        title: t("company_profile.toast.error"),
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const uploadCompanyImage = async (file: File, kind: "logo" | "cover") => {
    try {
      if (!company) return;
      const bucket = (supabase as any).storage.from("company-assets");
      const path = `${company.id}/${kind}-${Date.now()}-${file.name}`;
      const inferredType = file.name.toLowerCase().endsWith('.svg')
        ? 'image/svg+xml'
        : (file.type || 'image/png');
      const { error: uploadError } = await bucket.upload(path, file, {
        upsert: true,
        contentType: inferredType,
      });
      if (uploadError) throw uploadError;

      const { data: publicData } = bucket.getPublicUrl(path);
      const publicUrl = publicData?.publicUrl as string | undefined;
      if (!publicUrl) throw new Error("Failed to obtain public URL");

      const update: any = kind === "logo" ? { logo_url: publicUrl } : { cover_image_url: publicUrl };
      const { error: updateError } = await supabase.from("companies").update(update).eq("id", company.id);
      if (updateError) throw updateError;

      toast({ title: t("company_profile.toast.image_uploaded"), description: kind === "logo" ? t("company_profile.toast.logo_updated") : t("company_profile.toast.cover_updated") });
      await checkAuthAndFetchData();
    } catch (e: any) {
      console.error('uploadCompanyImage error', { kind, message: e?.message, stack: e?.stack });
      toast({ title: t("company_profile.toast.upload_failed"), description: e.message || t("company_profile.toast.error"), variant: "destructive" });
    }
  };

  // handleDeleteService was removed: the bookings → services FK makes hard
  // deletes impossible and the soft-delete RLS path proved unreliable on
  // production. Vendors should use toggleServiceStatus (Deactivate) instead.


  const toggleServiceStatus = async (serviceId: string, currentStatus: boolean) => {
    try {
      if (!(await requireAAL2())) return;
      const { error } = await supabase
        .from("services")
        .update({ is_active: !currentStatus })
        .eq("id", serviceId);

      if (error) throw error;

      toast({
        title: t("company_profile.toast.service_toggled"),
        description: !currentStatus ? t("company_profile.toast.service_activated") : t("company_profile.toast.service_deactivated"),
      });

      checkAuthAndFetchData();
    } catch (error) {
      toast({
        title: t("company_profile.toast.error"),
        description: error.message,
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">{t('company_profile.loading')}</div>;
  }

  if (!company) {
    return <div className="flex items-center justify-center min-h-screen">{t('company_profile.company_not_found')}</div>;
  }

  return (
    <div key={lang} className="min-h-screen app-page p-4">
      <Seo title={t("company_profile.seo.title")} noindex canonicalPath="/company/profile" />
      {StepUpDialog}
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="app-panel flex flex-col gap-4 p-5 md:flex-row md:items-center">
          <Button variant="outline" size="sm" className="w-full md:w-auto" onClick={() => navigate("/company/dashboard")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('company_profile.back_to_dashboard')}
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="text-3xl font-bold text-foreground break-words">{t('company_profile.title')}</h1>
            <p className="text-muted-foreground break-words">{t('company_profile.subtitle')}</p>
          </div>
          <LanguageSwitcher compact className="shrink-0" />
        </div>

        <Tabs defaultValue={(searchParams.get("tab") as any) || "company"} className="space-y-6">
          <div className="app-panel flex flex-col gap-4 p-4 xl:flex-row xl:items-center xl:justify-between">
            <TabsList className="flex h-auto w-full flex-nowrap items-center gap-2 overflow-x-auto overflow-y-hidden bg-transparent p-0 shadow-none xl:w-auto">
              <TabsTrigger value="company" className="shrink-0">
                <Building className="h-4 w-4 mr-2" />
                {t('company_profile.tab.company_info')}
              </TabsTrigger>
              <TabsTrigger value="services" className="shrink-0">
                <Settings className="h-4 w-4 mr-2" />
                {t('company_profile.tab.services')}
              </TabsTrigger>
              <TabsTrigger value="security" className="shrink-0">
                <Shield className="h-4 w-4 mr-2" />
                {t('company_profile.tab.security')}
              </TabsTrigger>
            </TabsList>
            <div className="flex flex-col gap-2 sm:flex-row xl:shrink-0">
              <Button variant="outline" onClick={exportData}>{t('company_profile.export_data')}</Button>
              <Button variant="destructive" onClick={deleteAccount}>{t('company_profile.delete_account')}</Button>
            </div>
          </div>

          <TabsContent value="company">
            <Card className="overflow-hidden">
              <CardHeader>
                <CardTitle className="break-words">{t('company_profile.company_information')}</CardTitle>
                <CardDescription className="break-words">
                  {t('company_profile.update_details')}
                </CardDescription>
              </CardHeader>
              <CardContent className="max-w-full overflow-x-auto">
                {/* Privacy & Consent */}
                <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4 p-4 border rounded-lg min-w-0">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>{t('company_profile.vendor_data_consent')}</Label>
                      <p className="text-xs text-muted-foreground">{t('company_profile.allow_aggregated_data','Consenti l\'uso di dati operativi aggregati e anonimizzati.')}</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={vendorConsent}
                      onChange={async (e) => {
                        const val = e.target.checked;
                        setVendorConsent(val);
                        const { error } = await (supabase as any).from('profiles').update({ vendor_consent: val } as any).eq('user_id', (await supabase.auth.getUser()).data.user?.id);
                        if (error) {
                          toast({ title: t("company_profile.toast.save_failed"), description: error.message, variant: "destructive" });
                          setVendorConsent(!val);
                        } else {
                          toast({ title: t("company_profile.toast.saved") });
                        }
                      }}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>{t('company_profile.customer_data_consent')}</Label>
                      <p className="text-xs text-muted-foreground">{t('company_profile.gate_exports_pii')}</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={customerConsent}
                      onChange={async (e) => {
                        const val = e.target.checked;
                        setCustomerConsent(val);
                        const { error } = await (supabase as any).from('profiles').update({ customer_consent: val } as any).eq('user_id', (await supabase.auth.getUser()).data.user?.id);
                        if (error) {
                          toast({ title: t("company_profile.toast.save_failed"), description: error.message, variant: "destructive" });
                          setCustomerConsent(!val);
                        } else {
                          toast({ title: t("company_profile.toast.saved") });
                        }
                      }}
                    />
                  </div>
                </div>
                {/* Logo & Cover upload */}
                {company && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 min-w-0">
                    <div className="min-w-0">
                      <Label>{t('company_profile.logo')}</Label>
                      <div className="mt-2 flex items-center gap-3">
                        <img src={company.logo_url || ""} alt="Logo" className="w-16 h-16 rounded-lg object-cover border" />
                        <Input type="file" accept="image/*" onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) uploadCompanyImage(f, "logo");
                        }} />
                      </div>
                    </div>
                    <div className="md:col-span-2 min-w-0">
                      <Label>{t('company_profile.cover_image')}</Label>
                      <div className="mt-2 flex items-center gap-3 overflow-x-auto">
                        <img src={company.cover_image_url || ""} alt="Cover" className="h-16 rounded-lg object-cover border w-full max-w-xs sm:w-64" />
                        <Input type="file" accept="image/*" onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) uploadCompanyImage(f, "cover");
                        }} />
                      </div>
                    </div>
                  </div>
                )}
                <Form {...companyForm}>
                  <form onSubmit={companyForm.handleSubmit(handleCompanyUpdate)} className="space-y-6">
                    {/* Plan & Commission */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 min-w-0">
                      <div className="min-w-0">
                        <Label>{t('company_profile.plan_tier')}</Label>
                        <Select disabled>
                          <SelectTrigger>
                            <SelectValue placeholder={(company as any)?.plan_tier || 'free'} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="free">Free (15%)</SelectItem>
                            <SelectItem value="pro">Pro (9–10%)</SelectItem>
                            <SelectItem value="scale">Scale (5–7%)</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground mt-1">
                          {t('company_profile.plan_tier_hint')}
                        </p>
                      </div>
                      <div className="min-w-0">
                        <Label>{t('company_profile.commission_rate')}</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={(company as any)?.commission_rate ?? 0.12}
                          readOnly
                          disabled
                        />
                        <p className="text-xs text-muted-foreground">
                          {t('company_profile.decimal_example')} — {t('company_profile.readonly_admin')}
                        </p>
                        <div className="mt-3 rounded-md border p-3 bg-muted/30">
                          <div className="text-sm font-medium">
                            {t('company_profile.effective_platform_fee')}
                          </div>
                          <div className="text-sm mt-1">
                            {(() => {
                              const feeBps = (company as any)?.platform_fee_bps;
                              const rate = (company as any)?.commission_rate;
                              if (typeof feeBps === 'number') {
                                const pct = (feeBps / 100).toFixed(2);
                                return `${pct}% (da platform_fee_bps = ${feeBps})`;
                              }
                              if (typeof rate === 'number') {
                                const pct = (Number(rate) * 100).toFixed(2);
                                return `${pct}% (da commission_rate = ${String(rate)})`;
                              }
                              return t('company_profile.effective_default');
                            })()}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 min-w-0">
                      <FormField
                        control={companyForm.control}
                        name="company_name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('company_profile.company_name')}</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={companyForm.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('company_profile.email')}</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={companyForm.control}
                      name="description"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('company_profile.description')}</FormLabel>
                          <FormControl>
                            <Textarea rows={4} {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 min-w-0">
                      <FormField
                        control={companyForm.control}
                        name="address"
                        render={({ field }) => (
                          <FormItem className="md:col-span-2">
                            <FormLabel>{t('company_profile.address')}</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={companyForm.control}
                        name="city"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('company_profile.city')}</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 min-w-0">
                      <FormField
                        control={companyForm.control}
                        name="postal_code"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('company_profile.postal_code')}</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={companyForm.control}
                        name="phone"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('company_profile.phone')}</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={companyForm.control}
                        name="tax_id"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('company_profile.tax_id')}</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    {/* Legal/Fiscal details */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 min-w-0">
                      <FormField control={companyForm.control} name="legal_name" render={({ field }) => (
                        <FormItem className="md:col-span-2">
                          <FormLabel>{t('company_profile.legal_name')}</FormLabel>
                          <FormControl><Input placeholder={t("company_profile.placeholder.legal_name")} {...field} /></FormControl>
                          <FormMessage />
                        </FormItem>
                      )} />
                      <FormField control={companyForm.control} name="vat_number" render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('company_profile.vat_number')}</FormLabel>
                          <FormControl><Input placeholder={t("company_profile.placeholder.vat")} {...field} /></FormControl>
                          <FormMessage />
                        </FormItem>
                      )} />
                      <FormField control={companyForm.control} name="sdi_code" render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('company_profile.sdi_code')}</FormLabel>
                          <FormControl><Input placeholder={t("company_profile.placeholder.sdi")} {...field} /></FormControl>
                          <FormMessage />
                        </FormItem>
                      )} />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 min-w-0">
                      <FormField control={companyForm.control} name="pec_email" render={({ field }) => (
                        <FormItem className="md:col-span-1">
                          <FormLabel>{t('company_profile.pec_email')}</FormLabel>
                          <FormControl><Input placeholder={t("company_profile.placeholder.pec")} {...field} /></FormControl>
                          <FormMessage />
                        </FormItem>
                      )} />
                    </div>

                    <FormField
                      control={companyForm.control}
                      name="website"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('company_profile.website_optional')}</FormLabel>
                          <FormControl>
                            <Input {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 min-w-0">
                      <FormField control={companyForm.control} name="website_url" render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('company_profile.website_url')}</FormLabel>
                          <FormControl><Input placeholder="https://yourcompany.com" {...field} /></FormControl>
                          <FormMessage />
                        </FormItem>
                      )} />
                      <FormField control={companyForm.control} name="facebook_url" render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('company_profile.facebook')}</FormLabel>
                          <FormControl><Input placeholder="https://facebook.com/yourpage" {...field} /></FormControl>
                          <FormMessage />
                        </FormItem>
                      )} />
                      <FormField control={companyForm.control} name="linkedin_url" render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('company_profile.linkedin')}</FormLabel>
                          <FormControl><Input placeholder="https://linkedin.com/company/yourcompany" {...field} /></FormControl>
                          <FormMessage />
                        </FormItem>
                      )} />
                      <FormField control={companyForm.control} name="instagram_url" render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('company_profile.instagram')}</FormLabel>
                          <FormControl><Input placeholder="https://instagram.com/yourhandle" {...field} /></FormControl>
                          <FormMessage />
                        </FormItem>
                      )} />
                      <FormField control={companyForm.control} name="whatsapp_url" render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('company_profile.whatsapp')}</FormLabel>
                          <FormControl><Input placeholder="https://wa.me/1234567890" {...field} /></FormControl>
                          <FormMessage />
                        </FormItem>
                      )} />
                    </div>

                    <Button type="submit">{t('company_profile.update_company_btn')}</Button>
                  </form>
                </Form>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="services" className="space-y-6">
            {/* Services List — editing happens in the ServiceBuilder on the dashboard, single source of truth */}
            <Card className="overflow-hidden">
              <CardHeader className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                <div className="min-w-0">
                  <CardTitle className="break-words">
                    {t('company_profile.services.title')} ({services.length})
                  </CardTitle>
                  <CardDescription className="break-words">
{t('company_profile.services.subtitle')}
                  </CardDescription>
                </div>
                <Button
                  size="sm"
                  onClick={() => navigate('/company/dashboard?tab=services&new=1')}
                  className="shrink-0"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {t('company_profile.services.create_new')}
                </Button>
              </CardHeader>
              <CardContent className="max-w-full overflow-x-auto">
                {services.length === 0 ? (
                  <div className="text-center py-8">
                    <Settings className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-medium">
                      {t('company_profile.services.empty_title')}
                    </h3>
                    <p className="text-muted-foreground">
                      {t('company_profile.services.empty_help')}
                    </p>
                    <Button
                      className="mt-4"
                      onClick={() => navigate('/company/dashboard?tab=services&new=1')}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      {t('company_profile.services.create_new')}
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {services.map((service) => (
                      <div key={service.id} className="flex items-center justify-between p-4 border rounded-lg flex-wrap gap-3">
                        <div className="space-y-1 min-w-0">
                          <div className="flex items-center gap-3 flex-wrap">
                            <h4 className="font-medium break-words">{service.name}</h4>
                            <Badge variant={service.is_active ? "default" : "secondary"}>
                              {service.is_active
                                ? t('company_profile.services.status_active')
                                : t('company_profile.services.status_inactive')}
                            </Badge>
                            <Badge variant="outline">
                              {service.service_categories?.name}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground line-clamp-2 break-words">
                            {service.description}
                          </p>
                          <p className="text-sm font-medium">
                            {formatCurrency(service.base_price)} • {service.duration_minutes}min • {service.pricing_type}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => toggleServiceStatus(service.id, service.is_active)}
                          >
                            {service.is_active
                              ? t('company_profile.services.deactivate')
                              : t('company_profile.services.activate')}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => navigate(`/company/dashboard?tab=services&edit=${service.id}`)}
                            aria-label={t('company_profile.services.edit')}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          {/* Delete button intentionally removed: the FK from
                              bookings.service_id blocks a hard delete, and the
                              RLS soft-delete path is brittle. Vendors should
                              use Deactivate to take a service offline. */}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="security" className="space-y-6">
            <Card className="app-panel overflow-hidden border border-slate-200/70">
              <CardHeader>
                <CardTitle className="text-slate-950">{t('company_profile.security.title')}</CardTitle>
                <CardDescription className="text-slate-600">{t('company_profile.security.desc')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="rounded-[1.25rem] border border-slate-200/80 bg-white/90 p-4 shadow-soft flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-slate-950">{t('company_profile.security.password')}</h3>
                    <p className="text-sm text-slate-600">{t('company_profile.security.password_desc')}</p>
                  </div>
                  <Button variant="outline" onClick={async () => {
                    try {
                      const { data: { session } } = await supabase.auth.getSession();
                      if (!session?.user?.email) { navigate('/auth'); return; }
                      const { error } = await supabase.auth.resetPasswordForEmail(session.user.email, { redirectTo: window.location.origin + '/auth/reset' });
                      if (error) throw error;
                      toast({ title: t('company_profile.toast.email_sent'), description: t('company_profile.toast.email_sent_desc') });
                    } catch (e: any) {
                      const msg = String(e?.message || "").toLowerCase();
                      if (msg.includes("rate limit")) {
                        toast({ title: t("profile.toast.rate_limit"), description: t("profile.toast.rate_limit_reset"), variant: "destructive" });
                      } else {
                        toast({ title: t("company_profile.toast.error"), description: e.message || t("profile.toast.update_failed_generic"), variant: "destructive" });
                      }
                    }
                  }}>
                    <Lock className="h-4 w-4 mr-2" />
                    {t('company_profile.security.password_btn')}
                  </Button>
                </div>

                <div className="rounded-[1.25rem] border border-slate-200/80 bg-white/90 p-4 shadow-soft flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-slate-950">{t('company_profile.security.2fa')}</h3>
                    <p className="text-sm text-slate-600">{t('company_profile.security.2fa_desc')}</p>
                  </div>
                  <Dialog onOpenChange={(open) => { if (open) loadMfa(); }}>
                    <DialogTrigger asChild>
                      <Button variant="outline">
                        <Shield className="h-4 w-4 mr-2" />
{mfaFactors.length > 0 ? t('profile.security.manage_2fa') : t('profile.security.enable_2fa')}
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg">
                      <DialogHeader>
                        <DialogTitle>{t('profile.mfa.dialog_title')}</DialogTitle>
                        <DialogDescription>{t('profile.mfa.dialog_desc')}</DialogDescription>
                      </DialogHeader>

                      <div className="space-y-4">
                        {mfaError && (
                          <div className="text-sm text-red-600 border border-red-200 bg-red-50 rounded p-2">
                            {mfaError}
                          </div>
                        )}

                        <div className="text-sm text-gray-700">
                          {t('profile.mfa.status_label')} <span className="font-medium">{mfaFactors.length > 0 ? t('profile.mfa.status_enabled') : t('profile.mfa.status_disabled')}</span>
                        </div>

                        {mfaFactors.length === 0 ? (
                          <>
                            <Button disabled={mfaLoading} onClick={startEnable2FA}>
{mfaLoading ? t('profile.mfa.starting') : t('profile.mfa.start_setup')}
                            </Button>

                            {mfaQrSvg && (
                              <div className="space-y-2">
                                <div className="text-sm text-gray-700">{t('profile.mfa.scan_qr')}</div>
                                <div className="border rounded p-3 bg-white" dangerouslySetInnerHTML={{ __html: mfaQrSvg }} />
                                {mfaUri && (
                                  <div className="text-xs text-gray-500 break-all">{t("profile.mfa.uri_hint")} {mfaUri}</div>
                                )}
                              </div>
                            )}

                            {mfaChallenge?.id && (
                              <div className="space-y-2">
                                <div className="text-sm text-gray-700">{t('profile.mfa.enter_code')}</div>
                                <InputOTP maxLength={6} value={mfaCode} onChange={setMfaCode}>
                                  <InputOTPGroup>
                                    <InputOTPSlot index={0} />
                                    <InputOTPSlot index={1} />
                                    <InputOTPSlot index={2} />
                                    <InputOTPSlot index={3} />
                                    <InputOTPSlot index={4} />
                                    <InputOTPSlot index={5} />
                                  </InputOTPGroup>
                                </InputOTP>
                                <Button disabled={mfaLoading || (mfaCode || '').replace(/\s/g,'').length !== 6} onClick={verifyEnable2FA}>
{mfaLoading ? t('profile.mfa.verifying') : t('profile.mfa.verify_enable')}
                                </Button>
                              </div>
                            )}
                          </>
                        ) : (
                          <>
                            <div className="text-sm text-gray-700">{t("profile.mfa.enabled_message")}</div>
                            <Button variant="destructive" disabled={mfaLoading} onClick={disable2FA}>
{mfaLoading ? t('profile.mfa.disabling') : t('profile.mfa.disable')}
                            </Button>
                          </>
                        )}
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
